"use strict";
/**
 * LastFmService.ts
 * Integrates Last.fm Scrobbling with Meci & SoundCloud
 *
 * Scrobbling enables automatic tracking of whatever song the user is currently playing on
 * SoundCloud (or Spotify / desktop players). Last.fm API returns `@attr.nowplaying = "true"`.
 *
 * Meci periodically polls `user.getRecentTracks` and mirrors the currently playing track
 * into SoundCloudService and user status in real-time.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.lastFmService = void 0;
const SoundCloudService_1 = require("./SoundCloudService");
const MusicFeatureService_1 = require("./MusicFeatureService");
const DEFAULT_LASTFM_API_KEY = 'b25b959554ed76058ac220b7b2e0a026';
class LastFmService {
    constructor() {
        this.username = '';
        this.apiKey = DEFAULT_LASTFM_API_KEY;
        this.isAutoSyncEnabled = true;
        this.pollingInterval = null;
        this.listeners = new Set();
        this.lastDetectedTrack = null;
        this.isPolling = false;
        this.lastPushKey = '';
        this.lastPushAt = 0;
        if (typeof window !== 'undefined') {
            this.initFromStorage();
            if (this.username) {
                setTimeout(() => this.pollNowPlaying(), 300);
                this.startPolling();
            }
        }
    }
    initFromStorage() {
        try {
            this.username = localStorage.getItem('meci_lastfm_username') || '';
            const storedKey = localStorage.getItem('meci_lastfm_api_key');
            if (!storedKey || storedKey === 'c030d9361ad257d07996c4b223fb3947') {
                this.apiKey = DEFAULT_LASTFM_API_KEY;
                localStorage.setItem('meci_lastfm_api_key', this.apiKey);
            }
            else {
                this.apiKey = storedKey;
            }
            this.isAutoSyncEnabled = localStorage.getItem('meci_lastfm_autosync') !== 'false';
        }
        catch {
            this.username = '';
            this.apiKey = DEFAULT_LASTFM_API_KEY;
            this.isAutoSyncEnabled = true;
        }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this.listeners.forEach(l => l());
    }
    getUsername() {
        return this.username;
    }
    setUsername(user) {
        this.username = user.trim();
        localStorage.setItem('meci_lastfm_username', this.username);
        this.notify();
        if (this.isAutoSyncEnabled && this.username) {
            this.pollNowPlaying();
            this.startPolling();
        }
        else if (!this.username) {
            this.stopPolling();
        }
    }
    isAutoSync() {
        return this.isAutoSyncEnabled;
    }
    setAutoSync(enabled) {
        this.isAutoSyncEnabled = enabled;
        localStorage.setItem('meci_lastfm_autosync', String(enabled));
        this.notify();
        if (enabled && this.username) {
            this.pollNowPlaying();
            this.startPolling();
        }
        else {
            this.stopPolling();
        }
    }
    getLastTrack() {
        return this.lastDetectedTrack;
    }
    startPolling() {
        this.stopPolling();
        // Poll every 8 seconds for real-time live detection
        this.pollingInterval = setInterval(() => {
            this.pollNowPlaying();
        }, 8000);
    }
    stopPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }
    async pollNowPlaying() {
        if (!this.username || this.isPolling)
            return null;
        this.isPolling = true;
        try {
            const url = `https://ws.audioscrobbler.com/2.0/?method=user.getrecenttracks&user=${encodeURIComponent(this.username)}&api_key=${this.apiKey}&format=json&limit=2`;
            const res = await fetch(url);
            if (!res.ok) {
                if (res.status === 400 || res.status === 403) {
                    this.apiKey = DEFAULT_LASTFM_API_KEY;
                    localStorage.setItem('meci_lastfm_api_key', this.apiKey);
                }
                throw new Error(`HTTP ${res.status}`);
            }
            const data = await res.json();
            const trackList = data?.recenttracks?.track;
            if (!trackList || (Array.isArray(trackList) && trackList.length === 0)) {
                SoundCloudService_1.soundCloudService.setTrack(null);
                return null;
            }
            const rawTrack = Array.isArray(trackList) ? trackList[0] : trackList;
            if (!rawTrack) {
                SoundCloudService_1.soundCloudService.setTrack(null);
                return null;
            }
            const isNowPlaying = rawTrack?.['@attr']?.nowplaying === 'true';
            const uts = Number(rawTrack.date?.uts || 0);
            // Considered live if explicitly nowplaying OR played within the last 4 minutes (240s)
            const isRecentlyPlayed = !isNowPlaying && uts > 0 && ((Date.now() / 1000) - uts) < 240;
            const isPlaying = isNowPlaying || isRecentlyPlayed;
            const trackName = rawTrack.name || '';
            const artistName = (typeof rawTrack.artist === 'string' ? rawTrack.artist : (rawTrack.artist?.['#text'] || rawTrack.artist?.name)) || 'Bilinmeyen Sanatçı';
            const albumName = (typeof rawTrack.album === 'string' ? rawTrack.album : (rawTrack.album?.['#text'] || rawTrack.album?.title)) || '';
            let artwork = '';
            if (Array.isArray(rawTrack.image)) {
                const largeImg = rawTrack.image.find((img) => img.size === 'extralarge') || rawTrack.image.find((img) => img.size === 'large') || rawTrack.image[0];
                artwork = largeImg?.['#text'] || '';
            }
            const trackUrl = rawTrack.url || 'https://www.last.fm';
            const track = {
                name: trackName,
                artist: artistName,
                album: albumName,
                artworkUrl: artwork,
                url: trackUrl,
                nowPlaying: isPlaying,
                timestamp: Date.now(),
            };
            this.lastDetectedTrack = track;
            this.notify();
            if (isPlaying) {
                // Automatically reflect onto live presence without manual switches!
                SoundCloudService_1.soundCloudService.setTrack({
                    name: track.name,
                    artist: track.artist,
                    trackUrl: track.url,
                    artworkUrl: track.artworkUrl,
                    durationMs: 210000,
                    progressMs: isNowPlaying ? 35000 : 180000,
                    isPlaying: true,
                });
            }
            else {
                SoundCloudService_1.soundCloudService.setTrack(null);
            }
            const pushKey = isPlaying ? `${track.name}|${track.artist}` : '';
            if (pushKey !== this.lastPushKey || (isPlaying && Date.now() - this.lastPushAt > 120000)) {
                this.lastPushKey = pushKey;
                this.lastPushAt = Date.now();
                MusicFeatureService_1.musicFeatureService.pushNowPlaying(isPlaying ? {
                    id: `lf_${Date.now()}`,
                    name: track.name,
                    artist: track.artist,
                    trackUrl: track.url,
                    artworkUrl: track.artworkUrl,
                    durationMs: 210000,
                    progressMs: 35000,
                    isPlaying: true,
                    updatedAt: Date.now(),
                } : null);
            }
            return track;
        }
        catch (err) {
            console.warn('[LastFmService] Poll error:', err.message);
            return null;
        }
        finally {
            this.isPolling = false;
        }
    }
}
exports.lastFmService = new LastFmService();
